/**
 * AdminAuditLogsPage — Full audit trail for all organization actions.
 */

import React, { useCallback, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, ChevronDown, Filter, ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  update: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  delete: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  login: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400',
  default: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400',
};

function getActionColor(action: string): string {
  const lower = action.toLowerCase();
  if (lower.includes('create') || lower.includes('insert')) return ACTION_COLORS.create;
  if (lower.includes('update') || lower.includes('edit')) return ACTION_COLORS.update;
  if (lower.includes('delete') || lower.includes('remove')) return ACTION_COLORS.delete;
  if (lower.includes('login')) return ACTION_COLORS.login;
  return ACTION_COLORS.default;
}

export const AdminAuditLogsPage: React.FC = () => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const filtersWidget = useMemo(() => (
    <div className="flex flex-1 items-center justify-end gap-1.5">
      <div className="relative">
        <select
          className={`${filterBtnClass} appearance-none cursor-pointer`}
          style={{ paddingLeft: 22, paddingRight: 22 }}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          <option value="">Todas ações</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="login">Login</option>
        </select>
        <Filter size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
        <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
      </div>
      <div className="flex w-[220px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar usuário, ação..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {search && (
            <button onClick={() => setSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [actionFilter, search]);

  const logsQuery = useQuery({
    queryKey: ['admin-audit-logs', orgId],
    enabled: Boolean(orgId),
    queryFn: () => AdminConsoleService.getAuditLogs(orgId, 200),
  });

  const logs = logsQuery.data ?? [];
  const filtered = logs.filter((l) => {
    const matchSearch =
      !search ||
      (l.user_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (l.user_email ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (l.action ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (l.resource_type ?? '').toLowerCase().includes(search.toLowerCase());
    const matchAction = !actionFilter || (l.action ?? '').toLowerCase().includes(actionFilter.toLowerCase());
    return matchSearch && matchAction;
  });

  type SortKey = 'user' | 'action' | 'resource' | 'ip' | 'date';
  type SortDir = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const handleSortChange = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(key === 'date' ? 'desc' : 'asc');
      return key;
    });
  }, []);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortKey) {
        case 'user':
          av = (a.user_name ?? a.user_email ?? '').toLowerCase();
          bv = (b.user_name ?? b.user_email ?? '').toLowerCase();
          break;
        case 'action':
          av = (a.action ?? '').toLowerCase();
          bv = (b.action ?? '').toLowerCase();
          break;
        case 'resource':
          av = (a.resource_type ?? a.resource_name ?? '').toLowerCase();
          bv = (b.resource_type ?? b.resource_name ?? '').toLowerCase();
          break;
        case 'ip':
          av = (a.ip_address ?? '').toLowerCase();
          bv = (b.ip_address ?? '').toLowerCase();
          break;
        case 'date':
          av = new Date(a.created_at).getTime();
          bv = new Date(b.created_at).getTime();
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const auditColumns: ReadonlyArray<{ key: SortKey; label: string }> = [
    { key: 'user', label: 'Usuário' },
    { key: 'action', label: 'Ação' },
    { key: 'resource', label: 'Objeto' },
    { key: 'ip', label: 'IP' },
    { key: 'date', label: 'Data/Hora' },
  ];

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout ribbonSlots={{ afterGroups: filtersWidget }}>
        <div className="enterprise-page h-full overflow-y-auto">
          {logsQuery.isPending && <ModuleLoadingState label="Carregando logs de auditoria" />}

          {!logsQuery.isPending && (
            <ModuleCard
              title={`Logs de Auditoria (${filtered.length})`}
            >
              {!logsQuery.data?.length ? (
                <p className="text-[12px] enterprise-text-secondary py-6 text-center">
                  Nenhum log de auditoria encontrado. A tabela <code className="font-mono">audit_logs</code> pode não estar configurada ou não há eventos registrados.
                </p>
              ) : !filtered.length ? (
                <p className="text-[12px] enterprise-text-secondary py-4 text-center">Nenhum resultado para os filtros aplicados.</p>
              ) : (
                <div className="enterprise-table-shell">
                  <table className="w-full text-left text-sm">
                    <thead className="enterprise-table-header">
                      <tr>
                        {auditColumns.map((col) => {
                          const active = sortKey === col.key;
                          const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                          return (
                            <th key={col.key} className="enterprise-table-head-cell">
                              <button
                                type="button"
                                onClick={() => handleSortChange(col.key)}
                                className="inline-flex items-center gap-1 text-left hover:text-[var(--text-primary)]"
                              >
                                <span>{col.label}</span>
                                <Icon size={10} className={active ? '' : 'opacity-40'} />
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((log) => (
                        <tr key={log.id} className="enterprise-table-row">
                          <td className="enterprise-table-cell">
                            <div>
                              {log.user_name && (
                                <p className="font-medium enterprise-text-primary text-[12px]">{log.user_name}</p>
                              )}
                              {log.user_email && (
                                <p className="font-mono text-[11px] enterprise-text-secondary">{log.user_email}</p>
                              )}
                              {!log.user_name && !log.user_email && (
                                <span className="enterprise-text-secondary">—</span>
                              )}
                            </div>
                          </td>
                          <td className="enterprise-table-cell">
                            <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${getActionColor(log.action)}`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="enterprise-table-cell enterprise-text-secondary">
                            {log.resource_type && (
                              <span className="font-mono text-[11px]">{log.resource_type}</span>
                            )}
                            {log.resource_name && (
                              <span className="block text-[11px]">"{log.resource_name}"</span>
                            )}
                            {!log.resource_type && !log.resource_name && '—'}
                          </td>
                          <td className="enterprise-table-cell font-mono text-[11px] enterprise-text-secondary">
                            {log.ip_address || '—'}
                          </td>
                          <td className="enterprise-table-cell enterprise-text-secondary tabular-nums text-[12px]">
                            {new Date(log.created_at).toLocaleString('pt-BR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ModuleCard>
          )}
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
